import { Suspense, type ReactNode } from "react";
import { Outlet } from "react-router-dom";
import { PageLoading } from "@/lib/lazy-page";
import { AppSidebar } from "@/components/AppSidebar";
import {
  SidebarProvider,
  SidebarTrigger,
  SidebarInset,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { RightSidebarProvider } from "@/contexts/RightSidebarContext";
import { PageHeaderProvider } from "@/contexts/PageHeaderContext";
import { NotificationBell } from "@/components/layout/NotificationBell";
import {
  GLOBAL_SEARCH_OPEN_EVENT,
  ProjectSwitcher,
} from "@/components/ProjectSwitcher";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { OfflineStatusBadge } from "@/components/OfflineStatusBadge";
import { OfflineEditGuard } from "@/components/OfflineEditGuard";
import { NotificationsProvider } from "@/hooks/useNotifications";
import { PanelProvider } from "@/navigation/PanelProvider";
import { SidePanel } from "@/navigation/SidePanel";
import { PageLocation } from "@/navigation/LocationTrail";
import { ContextActionsMenu } from "@/navigation/ContextActionsMenu";
import { useIsMobile } from "@/hooks/use-mobile";
import { MAIN_SCROLL_ID, NavigationTracker } from "@/navigation/origin";

/** Desktop shows the entity actions in the sidebar's context header. */
function MobileContextActions() {
  const isMobile = useIsMobile();
  return isMobile ? <ContextActionsMenu className="h-8 w-8" /> : null;
}

export function MainLayout({ panelRoutes }: { panelRoutes: ReactNode }) {
  const openSearch = () => {
    window.dispatchEvent(new Event(GLOBAL_SEARCH_OPEN_EVENT));
  };

  return (
    <SidebarProvider className="h-dvh overflow-hidden">
      <RightSidebarProvider>
        <PageHeaderProvider>
          <NotificationsProvider>
            <PanelProvider>
              <NavigationTracker />
              <div className="h-dvh flex w-full overflow-hidden">
                <ProjectSwitcher />
                <AppSidebar />
                <div className="relative flex flex-1 min-w-0 overflow-hidden">
                  <SidebarInset className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
                    <header className="flex h-12 md:h-14 shrink-0 items-center gap-2 border-b border-border px-3 md:px-4">
                      <SidebarTrigger className="-ml-1" />
                      <Separator orientation="vertical" className="h-6" />
                      <div className="flex-1 flex items-center min-w-0">
                        <PageLocation />
                      </div>
                      <MobileContextActions />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={openSearch}
                        allowOffline
                        className="hidden md:inline-flex items-center gap-2"
                        data-testid="global-search-button"
                      >
                        <Search className="h-4 w-4" />
                        <span>Search</span>
                        <span className="text-xs text-muted-foreground">
                          Cmd+K
                        </span>
                      </Button>
                      {/* P6: search is reachable on mobile too. */}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={openSearch}
                        allowOffline
                        className="md:hidden h-8 w-8"
                        aria-label="Search"
                        data-testid="global-search-button-mobile"
                      >
                        <Search className="h-4 w-4" />
                      </Button>
                      <OfflineStatusBadge />
                      <NotificationBell />
                    </header>
                    <main
                      id={MAIN_SCROLL_ID}
                      // Pages marked full bleed (geo plans on phones) run edge to edge
                      className="flex-1 overflow-auto overscroll-contain p-3 has-[[data-full-bleed]]:px-0 md:p-6"
                    >
                      <OfflineEditGuard>
                        {/* Keeps the sidebar and header while a page loads */}
                        <Suspense fallback={<PageLoading />}>
                          <Outlet />
                        </Suspense>
                      </OfflineEditGuard>
                    </main>
                  </SidebarInset>
                  <SidePanel routes={panelRoutes} />
                </div>
              </div>
            </PanelProvider>
          </NotificationsProvider>
        </PageHeaderProvider>
      </RightSidebarProvider>
    </SidebarProvider>
  );
}
